"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  ArrowDown,
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code2,
  Dices,
  Hash,
  PenLine,
  Search,
  Trophy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format-datetime";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/components/providers/auth-provider";
import { JudgeDarkHead } from "./shell/JudgeDarkHead";
import { LibraryMeter, LibraryProgress } from "./library/LibraryHeadParts";
import { DifficultyGlyph, DifficultyTag } from "./DifficultyTag";
import { SolvedMark } from "./SolvedMark";
import { SolveCount, solveCountLabels, type SolveCountLabels } from "./SolveCount";
import { TopicPill } from "./TopicPill";
import { RunTwiceTag } from "./RunTwiceTag";
import { InteractiveTag } from "./InteractiveTag";
import { FunctionTag } from "./workspace/FunctionSignatureCard";
import { ProblemRatingChip } from "./ProblemRatingChip";
import { fetchJudgeProblems } from "../api";
import { useJudgeProblems, useJudgeTopics } from "../hooks";
import { LIBRARY_PAGE_SIZE, topicIntro, topicName } from "../lib/topics";
import { PROBLEM_ANSWER_FILTERS, PROBLEM_DIFFICULTIES, PROBLEM_KIND_FILTERS, PROBLEM_LIST_SORTS, PROBLEM_RATING_RANGES } from "../types";
import type {
  DifficultyCounts,
  JudgeProblemListItem,
  JudgeProblemListResponse,
  ProblemAnswerFilter,
  ProblemDifficulty,
  ProblemKindFilter,
  ProblemListSort,
  ProblemRatingRangeKey,
  SolvedFilter,
  SolvedState,
} from "../types";

const PAGE_SIZE = LIBRARY_PAGE_SIZE;
const STATUS_FILTERS = ["solved", "attempted", "untried"] as const;

/** `EX-PRT01`: a rating range's bounds as the list query sends them. */
function ratingRangeParams(key: "" | ProblemRatingRangeKey): { ratingMin?: number; ratingMax?: number } {
  const range = PROBLEM_RATING_RANGES.find((entry) => entry.key === key);
  if (!range) return {};
  return {
    ...("min" in range ? { ratingMin: range.min } : {}),
    ...("max" in range ? { ratingMax: range.max } : {}),
  };
}

/**
 * `EX-TAG01` (ADR-0087): the library narrowed to one topic, on its own page.
 * `initial` is the server's first page in the topic's default order, shown
 * while the reader's session settles, so the page's problems are in the HTML
 * a crawler reads.
 */
export type CatalogTopic = {
  slug: string;
  byDifficulty: DifficultyCounts;
  initial: JudgeProblemListResponse | null;
};

/** How many topics the strip under the list shows. */
const TOPIC_STRIP_MAX = 24;

/**
 * The row's grid from `lg` up: status (signed in only), problem, topics, the
 * round it came from (only when a row on the page has one), difficulty with
 * the rating from the round beside it (`EX-PRT01`), Solved / Tried
 * (`EX-PLR01`) and the "Solve" arrow. Written out whole because
 * Tailwind reads class names statically.
 */
const DESKTOP_COLUMNS = {
  statusSource: "lg:grid-cols-[28px_minmax(0,1.5fr)_minmax(0,1.15fr)_200px_172px_140px_20px]",
  status: "lg:grid-cols-[28px_minmax(0,1.5fr)_minmax(0,1.15fr)_172px_140px_20px]",
  source: "lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.15fr)_200px_172px_140px_20px]",
  plain: "lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.15fr)_172px_140px_20px]",
} as const;

function desktopColumns(showStatus: boolean, showSource: boolean): string {
  if (showStatus) return showSource ? DESKTOP_COLUMNS.statusSource : DESKTOP_COLUMNS.status;
  return showSource ? DESKTOP_COLUMNS.source : DESKTOP_COLUMNS.plain;
}

/**
 * Below `lg` a row stacks, as on the phone board: the status in its own narrow
 * column, the title, topics and round under each other, the difficulty on the
 * right. The cells are the same elements at every width, placed explicitly
 * here and returned to the flow at `lg`, so nothing is rendered twice.
 */
const STACK = {
  withStatus: {
    row: "grid-cols-[22px_minmax(0,1fr)_auto]",
    status: "col-start-1 row-start-1 row-span-3 self-start pt-0.5",
    body: "col-start-2",
    side: "col-start-3 row-start-1 row-span-3 self-start",
  },
  noStatus: {
    row: "grid-cols-[minmax(0,1fr)_auto]",
    status: "",
    body: "col-start-1",
    side: "col-start-2 row-start-1 row-span-3 self-start",
  },
} as const;
const RESET = "lg:col-start-auto lg:row-start-auto lg:row-span-1 lg:self-center lg:pt-0";

function StatusMark({ state }: { state: SolvedState | null }) {
  const t = useTranslations("judge.catalog");
  const label = state === "solved" ? t("mark_solved") : state === "attempted" ? t("mark_attempted") : t("mark_untried");
  return <SolvedMark state={state} label={label} />;
}

/**
 * One problem. `EX-PUB20` settled the titles: the reader's language first, the
 * other one under it, a label only on an English-only problem. `EX-PUB21` adds
 * the round it came from and, from ten solvers, how many solved it.
 */
function ProblemRow({
  problem,
  locale,
  showStatus,
  showSource,
  countLabels,
}: {
  problem: JudgeProblemListItem;
  locale: string;
  showStatus: boolean;
  showSource: boolean;
  countLabels: SolveCountLabels;
}) {
  const t = useTranslations("judge.catalog");
  const tTopics = useTranslations("judge.topics");
  const title = locale === "bn" && problem.title_bn ? problem.title_bn : problem.title_en;
  const otherTitle = problem.title_bn ? (locale === "bn" ? problem.title_en : problem.title_bn) : null;
  const source = problem.source ?? null;
  const sourceTitle = source ? (locale === "bn" && source.title_bn ? source.title_bn : source.title_en) : null;
  const stack = showStatus ? STACK.withStatus : STACK.noStatus;

  return (
    <Link
      href={`/${locale}/judge/${problem.slug}`}
      className={cn(
        "group grid items-center gap-x-4 gap-y-1.5 border-b border-neutral-100 px-4 py-4 transition-colors duration-200 last:border-b-0 hover:bg-primary/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-6 lg:min-h-[76px] lg:gap-x-5 lg:py-3",
        stack.row,
        desktopColumns(showStatus, showSource),
      )}
    >
      {showStatus ? (
        <span className={cn(stack.status, RESET)}>
          <StatusMark state={problem.solvedState} />
        </span>
      ) : null}

      <span className={cn("min-w-0 row-start-1", stack.body, RESET)}>
        <span className="flex flex-wrap items-center gap-2">
          <span className="break-words text-base font-bold tracking-tight text-neutral-900">{title}</span>
          {problem.kind === "run_twice" ? <RunTwiceTag size="sm" /> : null}
          {problem.kind === "interactive" ? <InteractiveTag size="sm" /> : null}
          {problem.answer === "function" ? <FunctionTag size="sm" /> : null}
          {otherTitle ? null : (
            <span className="rounded-full border border-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-500">
              {t("englishOnly")}
            </span>
          )}
        </span>
        {otherTitle ? <p className="mt-0.5 text-[13.5px] text-neutral-400">{otherTitle}</p> : null}
      </span>

      {problem.tags.length > 0 ? (
        <span className={cn("row-start-2 flex min-w-0 flex-wrap gap-1.5", stack.body, RESET)}>
          {problem.tags.map((tag) => (
            <TopicPill key={tag} tag={tag} name={topicName(tTopics, tag)} locale={locale} link={false} />
          ))}
        </span>
      ) : (
        <span className="hidden lg:block" aria-hidden />
      )}

      {showSource ? (
        source && sourceTitle ? (
          <span
            data-testid="problem-source"
            className={cn("row-start-3 inline-flex min-w-0 items-center gap-1.5 text-[13px] text-neutral-500", stack.body, RESET)}
          >
            <Trophy className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            {/* A long round name gives way; the letter never does. */}
            <span className="truncate">{sourceTitle}</span>
            <span className="shrink-0">· {source.letter}</span>
          </span>
        ) : (
          <span className="hidden lg:block" aria-hidden />
        )
      ) : null}

      {/* Below `lg` the tag and the count stack on the right; from `lg` each is its own column. */}
      <span className={cn("flex flex-col items-end gap-2 text-right lg:contents", stack.side, RESET)}>
        <span className="flex flex-wrap items-center justify-end gap-1.5 lg:justify-self-start lg:justify-start">
          <DifficultyTag difficulty={problem.difficulty} label={t(`difficulty_${problem.difficulty}`)} />
          {problem.rating ? <ProblemRatingChip rating={problem.rating} locale={locale} /> : null}
        </span>
        <SolveCount
          solvers={problem.solvers}
          attempters={problem.attempters}
          submissions={problem.submissions}
          locale={locale}
          labels={countLabels}
          className="lg:justify-self-end"
        />
      </span>

      <ArrowRight
        className="hidden h-4 w-4 justify-self-end text-primary-ink opacity-0 transition-[opacity,transform] duration-200 -translate-x-1 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 motion-reduce:transition-none lg:block"
        aria-hidden
      />
    </Link>
  );
}

function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function chipClass(on: boolean): string {
  return cn(
    "inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-[9px] px-3 text-[13.5px] font-medium transition-colors duration-200",
    on
      ? "bg-white text-neutral-900 shadow-sm ring-1 ring-neutral-200"
      : "text-neutral-600 hover:text-neutral-900",
  );
}

/**
 * `/judge/problems`, the problem library: `EX-PUB01`'s catalogue, redrawn in
 * `EX-PUB21` to the "Judge pages" proposal the founder accepted on 2026-09-23.
 *
 * A dark head with the library's difficulty meter and the reader's own
 * progress (or the reason to sign in), then one tools card: search (title or
 * topic, and the Judge bar's `/` lands here), difficulty chips that count what
 * choosing them would show, the topic, the reader's status and a random
 * problem. Everything still reads signed out, and the status column and filter
 * exist only for a reader they can describe.
 */
export function JudgeCatalogClient({ locale, topic }: { locale: string; topic?: CatalogTopic }) {
  const t = useTranslations("judge.catalog");
  const tTopics = useTranslations("judge.topics");
  const tCount = useTranslations("judge.solveCount");
  const countLabels = useMemo(() => solveCountLabels(tCount, locale), [tCount, locale]);
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // `EX-TAG01`: a topic page fixes the tag and opens on "Most solved".
  const defaultSort: ProblemListSort = topic ? "solved" : "newest";
  const [search, setSearch] = useState("");
  const [difficulty, setDifficulty] = useState<"" | ProblemDifficulty>("");
  const [tag, setTag] = useState(topic?.slug ?? "");
  const [status, setStatus] = useState<"" | SolvedFilter>("");
  const [sort, setSort] = useState<ProblemListSort>(defaultSort);
  // `EX-RTW03`: the Kind filter.
  const [kind, setKind] = useState<"" | ProblemKindFilter>("");
  // `EX-FNC05`: the Answer filter, function problems or whole programs.
  const [answer, setAnswer] = useState<"" | ProblemAnswerFilter>("");
  // `EX-PRT01`: the rating filter.
  const [ratingRange, setRatingRange] = useState<"" | ProblemRatingRangeKey>("");
  const [page, setPage] = useState(1);
  const [randomBusy, setRandomBusy] = useState(false);

  const debouncedSearch = useDebounced(search, 300);
  const searchRef = useRef<HTMLInputElement>(null);
  const isTopicPage = topic !== undefined;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, difficulty, tag, status, sort, kind, answer, ratingRange]);

  /**
   * `EX-PUB19`: the Judge bar's `/` shortcut lands here with `?focus=search`
   * from any other page. Read once on mount rather than through
   * `useSearchParams`, which would pull this statically rendered page onto the
   * client behind a Suspense boundary for one boolean.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("focus") === "search") {
      searchRef.current?.focus();
    }
    // `EX-PRF02`: a topic row on a profile lands here with `?tag=`. A topic
    // page's tag is its own and is never read from the address.
    const tagParam = params.get("tag")?.trim().toLowerCase();
    if (tagParam && !isTopicPage) setTag(tagParam);
    // `EX-TAG01`: `?sort=solved` opens on "Most solved"; `EX-PRT01` adds the rating orders.
    const sortParam = params.get("sort");
    const sortMatch = PROBLEM_LIST_SORTS.find((value) => value === sortParam);
    if (sortMatch) setSort(sortMatch);
    // `CR-RED01`: a Career Path roadmap step lands here with `?difficulty=` too.
    const difficultyParam = params.get("difficulty");
    if (difficultyParam === "easy" || difficultyParam === "medium" || difficultyParam === "hard") {
      setDifficulty(difficultyParam);
    }
    // `EX-FNC05`: `?answer=function` opens on the function problems.
    const answerParam = PROBLEM_ANSWER_FILTERS.find((value) => value === params.get("answer"));
    if (answerParam) setAnswer(answerParam);
  }, [isTopicPage]);

  // A signed-out viewer must never send `solved=` — the server refuses it.
  const effectiveStatus = isAuthenticated ? status : "";
  const viewerKey = authLoading ? "loading" : isAuthenticated ? "member" : "anon";

  const params = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(difficulty ? { difficulty } : {}),
      ...(tag ? { tag } : {}),
      ...(effectiveStatus ? { solved: effectiveStatus } : {}),
      ...(sort !== "newest" ? { sort } : {}),
      ...(kind ? { kind } : {}),
      ...(answer ? { answer } : {}),
      ...ratingRangeParams(ratingRange),
    }),
    [page, debouncedSearch, difficulty, tag, effectiveStatus, sort, kind, answer, ratingRange],
  );

  // Held until auth bootstrap settles so the anonymous answer (solved state
  // null) is never fetched, cached and flashed at a signed-in viewer.
  const query = useJudgeProblems(params, viewerKey, !authLoading);
  const topics = useJudgeTopics();

  // `EX-TAG01`: the server's first page stands in until the list's own query
  // answers, and only for the view it describes (page 1, the default order,
  // no other filter), so it never passes for a filtered answer.
  const showsInitial =
    !!topic?.initial && page === 1 && sort === defaultSort && !debouncedSearch.trim() && !difficulty && !status && !kind && !answer && !ratingRange;
  const data = query.data ?? (showsInitial ? (topic?.initial ?? undefined) : undefined);
  const isPending = query.isPending && !data;
  const isError = query.isError && !data;

  const rows = data?.problems ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasFilter = !!(debouncedSearch.trim() || difficulty || (tag && !topic) || effectiveStatus || kind || answer || ratingRange);
  const showSource = rows.some((row) => row.source);
  const facets = data?.facets?.difficulty;
  const facetTotal = facets ? facets.easy + facets.medium + facets.hard : null;
  const n = (value: number) => formatNumber(value, locale);

  const clearFilters = () => {
    setSearch("");
    setDifficulty("");
    if (!topic) setTag("");
    setStatus("");
    setKind("");
    setAnswer("");
    setRatingRange("");
  };

  const topicItems = topics.data?.items ?? [];
  const topicTitle = topic ? topicName(tTopics, topic.slug) : null;
  const otherTopics = topicItems.filter((item) => item.slug !== topic?.slug).slice(0, TOPIC_STRIP_MAX);

  /**
   * One problem at random from whatever the filters match: the list's own
   * query for a single row at a random offset, so it honours search, topic,
   * difficulty and status without a route of its own.
   */
  const openRandom = async () => {
    const total = data?.total ?? 0;
    if (total === 0 || randomBusy) return;
    setRandomBusy(true);
    try {
      const offset = Math.floor(Math.random() * total);
      const pick = await fetchJudgeProblems({ ...params, page: offset + 1, limit: 1 });
      const slug = pick.problems[0]?.slug;
      if (slug) router.push(`/${locale}/judge/${slug}`);
    } catch {
      // A failed pick leaves the reader where they are; the list is still usable.
    } finally {
      setRandomBusy(false);
    }
  };

  const from = data && rows.length > 0 ? (data.page - 1) * PAGE_SIZE + 1 : 0;
  const to = data ? from + rows.length - 1 : 0;

  return (
    <>
      <JudgeDarkHead
        eyebrow={topic ? t("topicEyebrow") : t("eyebrow")}
        eyebrowIcon={topic ? <Hash className="h-4 w-4" /> : <Code2 className="h-4 w-4" />}
        title={topicTitle ? t("topicTitle", { name: topicTitle }) : t("title")}
        titleId="judge-library-title"
        description={
          topic ? (topicIntro(tTopics, topic.slug) ?? t("topicSubtitle", { name: topicTitle ?? topic.slug })) : t("subtitle")
        }
        aside={
          authLoading || topic ? null : (
            <LibraryProgress
              locale={locale}
              signedIn={isAuthenticated}
              viewer={data?.viewer}
              library={data?.library}
            />
          )
        }
      >
        {topic ? (
          <LibraryMeter library={topic.byDifficulty} locale={locale} />
        ) : data?.library ? (
          <LibraryMeter library={data.library} locale={locale} />
        ) : null}
      </JudgeDarkHead>

      <Container className="pb-16 pt-7 sm:pb-[72px]">
        {/* Tools */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3.5">
          <div className="relative min-w-[240px] flex-[1_1_320px]">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
            <Input
              ref={searchRef}
              data-judge-search=""
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("searchPlaceholder")}
              aria-label={t("searchPlaceholder")}
              className="h-11 w-full rounded-[10px] border border-neutral-200 bg-neutral-50 pl-10 pr-11 text-[14.5px] text-neutral-900 outline-none transition-colors duration-200 placeholder:text-neutral-400 focus:border-primary/50 focus:bg-white"
            />
            <kbd
              className="pointer-events-none absolute right-2.5 top-1/2 hidden h-[22px] min-w-[22px] -translate-y-1/2 items-center justify-center rounded-md border border-neutral-200 bg-white px-1.5 font-mono text-[11px] text-neutral-500 sm:inline-flex"
              aria-hidden
            >
              /
            </kbd>
          </div>

          <div
            role="group"
            aria-label={t("difficultyLabel")}
            className="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-neutral-100 p-1"
          >
            <button type="button" aria-pressed={difficulty === ""} onClick={() => setDifficulty("")} className={chipClass(difficulty === "")}>
              {t("difficultyAll")}
              {facetTotal !== null ? <span className="font-mono text-xs text-neutral-400">{n(facetTotal)}</span> : null}
            </button>
            {PROBLEM_DIFFICULTIES.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={difficulty === value}
                onClick={() => setDifficulty(value)}
                className={chipClass(difficulty === value)}
              >
                <DifficultyGlyph difficulty={value} />
                {t(`difficulty_${value}`)}
                {facets ? <span className="font-mono text-xs text-neutral-400">{n(facets[value])}</span> : null}
              </button>
            ))}
          </div>

          {topic ? (
            <Link
              href={`/${locale}/judge/problems`}
              className="inline-flex h-11 items-center gap-1.5 rounded-[10px] border border-neutral-200 bg-white px-3.5 text-sm text-neutral-700 transition-colors duration-200 hover:border-primary/40 hover:text-neutral-900"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              {t("allProblems")}
            </Link>
          ) : (
            <div className="relative">
              <select
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                aria-label={t("tagLabel")}
                className="h-11 appearance-none rounded-[10px] border border-neutral-200 bg-white pl-3.5 pr-9 text-sm text-neutral-900 outline-none transition-colors duration-200 focus:border-primary/50"
              >
                <option value="">{t("allTags")}</option>
                {topicItems.map((item) => (
                  <option key={item.slug} value={item.slug}>
                    {topicName(tTopics, item.slug)}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
            </div>
          )}

          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as ProblemListSort)}
              aria-label={t("sortLabel")}
              className="h-11 appearance-none rounded-[10px] border border-neutral-200 bg-white pl-3.5 pr-9 text-sm text-neutral-900 outline-none transition-colors duration-200 focus:border-primary/50"
            >
              {PROBLEM_LIST_SORTS.map((value) => (
                <option key={value} value={value}>
                  {t(`sort_${value}`)}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
          </div>

          <div className="relative">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as "" | ProblemKindFilter)}
              aria-label={t("kindLabel")}
              data-testid="catalog-kind"
              className={cn(
                "h-11 appearance-none rounded-[10px] border bg-white pl-3.5 pr-9 text-sm text-neutral-900 outline-none transition-colors duration-200 focus:border-primary/50",
                kind ? "border-primary/40" : "border-neutral-200",
              )}
            >
              <option value="">{t("kindAll")}</option>
              {PROBLEM_KIND_FILTERS.map((value) => (
                <option key={value} value={value}>
                  {value === "run_twice" ? t("kindRunTwice") : value === "interactive" ? t("kindInteractive") : t("kindStandard")}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
          </div>

          {/* `EX-FNC05`: how the answer is written, a whole program or one function. */}
          <div className="relative">
            <select
              value={answer}
              onChange={(e) => setAnswer(e.target.value as "" | ProblemAnswerFilter)}
              aria-label={t("answerLabel")}
              data-testid="catalog-answer"
              className={cn(
                "h-11 appearance-none rounded-[10px] border bg-white pl-3.5 pr-9 text-sm text-neutral-900 outline-none transition-colors duration-200 focus:border-primary/50",
                answer ? "border-primary/40" : "border-neutral-200",
              )}
            >
              <option value="">{t("answerAll")}</option>
              {PROBLEM_ANSWER_FILTERS.map((value) => (
                <option key={value} value={value}>
                  {value === "function" ? t("answerFunction") : t("answerProgram")}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
          </div>

          {/* `EX-PRT01`: the rating from the round, in the bands' ranges. */}
          <div className="relative">
            <select
              value={ratingRange}
              onChange={(e) => setRatingRange(e.target.value as "" | ProblemRatingRangeKey)}
              aria-label={t("ratingLabel")}
              data-testid="catalog-rating"
              className={cn(
                "h-11 appearance-none rounded-[10px] border bg-white pl-3.5 pr-9 text-sm text-neutral-900 outline-none transition-colors duration-200 focus:border-primary/50",
                ratingRange ? "border-primary/40" : "border-neutral-200",
              )}
            >
              <option value="">{t("ratingAll")}</option>
              {PROBLEM_RATING_RANGES.map((range) => (
                <option key={range.key} value={range.key}>
                  {t(`rating_${range.key}`, {
                    min: "min" in range ? n(range.min) : "",
                    max: "max" in range ? n(range.max) : "",
                    next: "max" in range ? n(range.max + 1) : "",
                  })}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden />
          </div>

          {isAuthenticated ? (
            <div
              role="group"
              aria-label={t("statusLabel")}
              className="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-neutral-100 p-1"
            >
              <button type="button" aria-pressed={status === ""} onClick={() => setStatus("")} className={chipClass(status === "")}>
                {t("status_all")}
              </button>
              {STATUS_FILTERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={status === value}
                  onClick={() => setStatus(value)}
                  className={chipClass(status === value)}
                >
                  {t(`status_${value}`)}
                </button>
              ))}
            </div>
          ) : null}

          <div className="ml-auto">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void openRandom()}
              disabled={!data || data.total === 0 || randomBusy}
              className="h-10 gap-2 rounded-[10px] px-3.5"
            >
              <Dices className="h-4 w-4" aria-hidden />
              {t("random")}
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="mt-4 overflow-hidden rounded-xl border border-neutral-200 bg-white">
          {rows.length > 0 ? (
            <div
              className={cn(
                "hidden min-h-[44px] items-center gap-x-5 bg-neutral-50 px-6 text-[11.5px] font-bold uppercase tracking-[0.1em] text-neutral-400 lg:grid",
                desktopColumns(isAuthenticated, showSource),
              )}
            >
              {/* The titles are for the eye (each row says it all); only the sort is a control. */}
              {isAuthenticated ? <span aria-hidden /> : null}
              <span aria-hidden>{t("colProblem")}</span>
              <span aria-hidden>{t("colTopics")}</span>
              {showSource ? <span aria-hidden>{t("colFrom")}</span> : null}
              <span aria-hidden>{t("colDifficulty")}</span>
              {/* `EX-PLR01`: the column's title sorts by it, "Most solved" and back. */}
              <Button
                type="button"
                variant="columnHead"
                size={null}
                aria-pressed={sort === "solved"}
                aria-label={tCount("sortMostSolved")}
                onClick={() => setSort(sort === "solved" ? "newest" : "solved")}
                className="justify-self-end"
              >
                {tCount("column")}
                <ArrowDown className="h-3 w-3" aria-hidden />
              </Button>
              <span aria-hidden />
            </div>
          ) : null}

          {isPending ? (
            <div>
              {Array.from({ length: 6 }).map((_unused, index) => (
                <div key={index} className="flex items-center gap-4 border-b border-neutral-100 px-6 py-5 last:border-b-0">
                  <Skeleton className="h-4 w-56" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="ml-auto h-6 w-16 rounded-full" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <p className="px-4 py-10 text-center text-sm text-neutral-500">{t("loadFailed")}</p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2.5 px-6 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Search className="h-5 w-5" aria-hidden />
              </span>
              <p className="mt-1 text-[17px] font-bold text-neutral-900">
                {hasFilter ? t("noMatchesTitle") : t("emptyTitle")}
              </p>
              <p className="max-w-sm text-sm text-neutral-500">{hasFilter ? t("noMatchesBody") : t("emptyBody")}</p>
              {hasFilter ? (
                <Button type="button" variant="secondary" onClick={clearFilters} className="mt-2 h-10 rounded-[10px]">
                  {t("clearFilters")}
                </Button>
              ) : (
                <Button asChild variant="secondary" className="mt-2 h-10 rounded-[10px]">
                  <Link href={`/${locale}/contests`}>{t("browseContests")}</Link>
                </Button>
              )}
            </div>
          ) : (
            rows.map((problem) => (
              <ProblemRow
                key={problem.id}
                problem={problem}
                locale={locale}
                showStatus={isAuthenticated}
                showSource={showSource}
                countLabels={countLabels}
              />
            ))
          )}

          {data && rows.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 bg-neutral-50 px-4 py-3.5 text-[13px] text-neutral-500 sm:px-6">
              <span>
                {t.rich("showing", {
                  from: n(from),
                  to: n(to),
                  total: n(data.total),
                  b: (chunks) => <b className="font-mono font-semibold text-neutral-900">{chunks}</b>,
                })}
              </span>
              {data.total > PAGE_SIZE ? (
                <span className="flex gap-2">
                  <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="h-9 gap-1 rounded-[9px] border border-neutral-200 bg-white px-3 text-[13px] text-neutral-700 hover:bg-neutral-50"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                    {t("previous")}
                  </Button>
                  <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="h-9 gap-1 rounded-[9px] border border-neutral-200 bg-white px-3 text-[13px] text-neutral-700 hover:bg-neutral-50"
                  >
                    {t("next")}
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* `EX-TAG01`: the topics, each its own page. */}
        {otherTopics.length > 0 ? (
          <nav aria-labelledby="judge-topics-title" className="mt-6 rounded-xl border border-neutral-200 bg-white px-5 py-4">
            <h2 id="judge-topics-title" className="text-[13px] font-bold uppercase tracking-[0.1em] text-neutral-500">
              {topic ? t("otherTopics") : t("browseTopics")}
            </h2>
            <ul className="mt-3 flex flex-wrap gap-2">
              {otherTopics.map((item) => (
                <li key={item.slug}>
                  <TopicPill
                    tag={item.slug}
                    name={`${topicName(tTopics, item.slug)} · ${n(item.problems)}`}
                    locale={locale}
                    className="h-8 px-3 text-[13px]"
                  />
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {/* Where problems come from */}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Link
            href={`/${locale}/contests`}
            className="group flex items-center gap-4 rounded-xl border border-neutral-200 bg-white px-5 py-5 transition-colors duration-200 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary" aria-hidden>
              <Trophy className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-bold text-neutral-900">{t("moreRoundsTitle")}</span>
              <span className="mt-1 block text-[13.5px] leading-normal text-neutral-500">{t("moreRoundsBody")}</span>
            </span>
            <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-primary-ink" aria-hidden />
          </Link>
          <Link
            href={`/${locale}/contests/set-a-problem`}
            className="group flex items-center gap-4 rounded-xl border border-neutral-200 bg-white px-5 py-5 transition-colors duration-200 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary" aria-hidden>
              <PenLine className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-[15px] font-bold text-neutral-900">{t("moreSetTitle")}</span>
              <span className="mt-1 block text-[13.5px] leading-normal text-neutral-500">{t("moreSetBody")}</span>
            </span>
            <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-primary-ink" aria-hidden />
          </Link>
        </div>
      </Container>
    </>
  );
}
